import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HubSectionContainer from '../CustomContainer';
import ProductCard from './ProductCard';
import {
  CAT_PRODUCTS, DOG_PRODUCTS, Pet, Product, focusSub, sectionTitle,
} from './productsData';
import { track } from '../analytics';
import useHubPageView from '../useHubPageView';

const PURPLE = '#3700B3';
const AFFILIATE_TEXT = 'Some links above are affiliate links, and Petcademy may earn a small commission when you buy through them.';

type Section = { focus: string, items: Product[] };

const matchesQuery = (product: Product, q: string): boolean => product.name.toLowerCase().includes(q)
  || product.focus.toLowerCase().includes(q)
  || sectionTitle(product.focus).toLowerCase().includes(q)
  || product.type.toLowerCase().includes(q);

const groupByFocus = (products: Product[]): Section[] => {
  const groups: Record<string, Product[]> = {};
  products.forEach((p) => {
    const key = p.focus || '';
    (groups[key] = groups[key] || []).push(p);
  });
  return Object.keys(groups)
    .sort((a, b) => sectionTitle(a).localeCompare(sectionTitle(b)))
    .map((focus) => ({
      focus,
      items: [...groups[focus]].sort((x, y) => x.name.localeCompare(y.name)),
    }));
};

function PetToggle({ pet, onChange, fullWidth }: { pet: Pet, onChange: (p: Pet) => void, fullWidth: boolean }) {
  const opts: { k: Pet, label: string }[] = [{ k: 'Dog', label: 'Dogs' }, { k: 'Cat', label: 'Cats' }];
  return (
    <Box sx={{
      display: fullWidth ? 'flex' : 'inline-flex',
      width: fullWidth ? '100%' : 'auto',
      background: '#EEEDF3',
      borderRadius: '999px',
      p: '4px',
      gap: '4px',
    }}
    >
      {opts.map((o) => {
        const active = pet === o.k;
        return (
          <Button
            key={o.k}
            disableElevation
            onClick={() => onChange(o.k)}
            sx={{
              flex: fullWidth ? 1 : 'unset',
              minWidth: 'unset',
              borderRadius: '999px',
              textTransform: 'none',
              p: fullWidth ? '10px 0' : '9px 26px',
              fontFamily: 'Lexend',
              fontWeight: 600,
              fontSize: { xs: 15, lg: 14.5 },
              background: active ? PURPLE : 'transparent',
              color: active ? '#fff' : 'rgba(0,0,0,0.60)',
              boxShadow: active ? '0 1px 3px rgba(55,0,179,0.3)' : 'none',
              '&:hover': { background: active ? PURPLE : 'rgba(0,0,0,0.04)' },
            }}
          >
            {o.label}
          </Button>
        );
      })}
    </Box>
  );
}

function SearchField({ value, onChange }: { value: string, onChange: (v: string) => void }) {
  const [focused, setFocused] = useState(false);
  return (
    <Box sx={{
      width: { xs: '100%', sm: 300 },
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      background: '#fff',
      border: `1px solid ${focused ? PURPLE : '#D9D6E2'}`,
      borderRadius: '999px',
      p: '10px 16px',
    }}
    >
      <SearchIcon sx={{ fontSize: 21, color: focused ? PURPLE : 'rgba(0,0,0,0.60)' }} />
      <Box
        component="input"
        type="text"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search products..."
        sx={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: 'Lexend',
          // 16px on mobile prevents iOS Safari from auto-zooming (and shifting/clipping the page) on focus
          fontSize: { xs: 16, sm: 15 },
          color: 'rgba(0,0,0,0.87)',
          minWidth: 0,
        }}
      />
      {value && (
        <Box
          component="button"
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', p: 0,
          }}
        >
          <CloseIcon sx={{ fontSize: 19, color: 'rgba(0,0,0,0.60)' }} />
        </Box>
      )}
    </Box>
  );
}

function CategorySection({ section, pet, viewLabel }: { section: Section, pet: Pet, viewLabel: string }) {
  return (
    <Box component="section" sx={{ mt: { xs: '26px', lg: '40px' } }}>
      <Box sx={{ mb: { xs: '13px', lg: '18px' } }}>
        <Typography sx={{
          fontFamily: 'Lexend', fontWeight: 700, fontSize: { xs: 17, lg: 20 }, color: '#1A1430', letterSpacing: '-0.2px',
        }}
        >
          {sectionTitle(section.focus)}
        </Typography>
        <Typography sx={{
          fontFamily: 'Lexend', fontSize: { xs: 12.5, lg: 14 }, color: 'rgba(0,0,0,0.60)', mt: '2px', lineHeight: 1.35,
        }}
        >
          {focusSub(section.focus, pet)}
        </Typography>
      </Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        gap: { xs: '12px', lg: '20px' },
      }}
      >
        {section.items.map((p) => (
          <ProductCard key={section.focus + p.name} product={p} viewLabel={viewLabel} species={pet.toLowerCase()} />
        ))}
      </Box>
    </Box>
  );
}

function NoMatches({ onClear }: { onClear: () => void }) {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      p: { xs: '48px 24px', lg: '64px 24px' },
      background: '#fff',
      borderRadius: '16px',
      border: '1px solid #ECEAF2',
      mt: { xs: '26px', lg: '40px' },
    }}
    >
      <Typography sx={{
        fontFamily: 'Lexend', fontWeight: 700, fontSize: { xs: 18, lg: 22 }, color: '#1A1430',
      }}
      >
        No products match those filters
      </Typography>
      <Typography sx={{
        fontFamily: 'Lexend', fontSize: { xs: 13.5, lg: 14.5 }, color: 'rgba(0,0,0,0.60)', mt: '8px', maxWidth: 420, lineHeight: 1.5,
      }}
      >
        Try a different search, or clear it to see the full set of trainer-picked products.
      </Typography>
      <Button
        onClick={onClear}
        startIcon={<RestartAltIcon />}
        sx={{
          mt: '22px',
          background: PURPLE,
          color: '#fff',
          borderRadius: '10px',
          textTransform: 'none',
          p: '11px 24px',
          fontFamily: 'Lexend',
          fontWeight: 600,
          fontSize: 14.5,
          '&:hover': { background: '#30009C' },
        }}
      >
        Clear Search
      </Button>
    </Box>
  );
}

function Products() {
  useHubPageView();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pet, setPet] = useState<Pet>('Dog');
  const [query, setQuery] = useState('');

  // Debounced so a single search fires once the user pauses typing, not per keystroke.
  useEffect(() => {
    if (!query) return undefined;
    const t = setTimeout(() => track('products_search', { search_text: query }), 800);
    return () => clearTimeout(t);
  }, [query]);

  const switchPet = (next: Pet) => {
    if (next === pet) return;
    track('products_filter_tab_click', { pet_type: next.toLowerCase() });
    setPet(next);
    setQuery('');
    window.scrollTo(0, 0);
  };

  const sections = useMemo(() => {
    const list = pet === 'Cat' ? CAT_PRODUCTS : DOG_PRODUCTS;
    const q = query.trim().toLowerCase();
    const results = q === '' ? list : list.filter((p) => matchesQuery(p, q));
    return groupByFocus(results);
  }, [pet, query]);

  const viewLabel = isMobile ? 'View' : 'View Product';

  return (
    <Box sx={{ background: '#FAF9FC', flexGrow: 1 }}>
      {/* header — matches the Community page treatment (sectionTitle + body2, bottom-bordered band) */}
      <Box sx={{ borderBottom: '1px solid #E0E0E0', background: '#fff' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">Recommended Products</Typography>
          <Typography variant="body2">
            Trainer-vetted products that support your pet&apos;s health, training and behavior.
          </Typography>
        </HubSectionContainer>
      </Box>

      <HubSectionContainer wide sx={{ pt: 3, pb: { xs: 4, lg: 6 } }}>
        {/* controls */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 2.25 },
          flexWrap: 'wrap',
        }}
        >
          <PetToggle pet={pet} onChange={switchPet} fullWidth={isMobile} />
          <SearchField value={query} onChange={setQuery} />
        </Box>

        {/* sections / empty state */}
        {sections.length === 0
          ? <NoMatches onClear={() => setQuery('')} />
          : sections.map((s) => (
            <CategorySection key={s.focus || 'other'} section={s} pet={pet} viewLabel={viewLabel} />
          ))}

        {/* affiliate disclosure */}
        <Box sx={{
          mt: { xs: '26px', lg: '36px' },
          pt: { xs: '16px', lg: '24px' },
          borderTop: '1px solid #ECEAF2',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        }}
        >
          <InfoOutlinedIcon sx={{
            fontSize: { xs: 16, lg: 18 }, color: 'rgba(0,0,0,0.60)', flexShrink: 0, mt: '1px',
          }}
          />
          <Typography sx={{
            fontFamily: 'Lexend', fontSize: { xs: 11.5, lg: 12.5 }, lineHeight: 1.6, color: 'rgba(0,0,0,0.60)', maxWidth: 760,
          }}
          >
            {AFFILIATE_TEXT}
          </Typography>
        </Box>
      </HubSectionContainer>
    </Box>
  );
}

export default Products;
